/**
 * Auth form field states, theme logo, and phone-prefix detection.
 */
import { Mail, Phone, type LucideIcon } from 'lucide-react';
import { COUNTRY_CODE_MAP } from '@/constants/phone-countries-catalog';
import type { LoginMode } from '@/hooks/useLogin';
import type { BrandingLogos } from '@/types/branding.types';
import { isValidEmail } from '@/utils/auth-validation-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  LOGIN_PASSWORD_MIN_LENGTH,
  PHONE_IDENTIFIER_MIN_LENGTH,
  SORTED_DIAL_CODES,
  type AuthFieldState,
  type SetupPasswordData,
} from '@/components/auth/auth-configs';

/** Dial code + remaining national number when the identifier starts with `+`. */
export interface PhonePrefixMatch {
  country: string;
  nationalNumber: string;
}

/** Leading icon for the identifier field (mail vs phone). */
export function getIdentifierIcon(mode: LoginMode): LucideIcon | undefined {
  if (mode === 'email') return Mail;
  if (mode === 'phone') return Phone;
  return undefined;
}

/** Password field state: untouched stays default; below the login floor is error. */
export function getLoginPasswordState(password: string, touched: boolean): AuthFieldState {
  if (!touched) return 'default';
  if (!password || password.length < LOGIN_PASSWORD_MIN_LENGTH) return 'error';
  return 'success';
}

/** Login password error after the field is touched. */
export function getLoginPasswordError(password: string, touched: boolean): string | undefined {
  if (!touched) return undefined;
  if (!password || password.length < LOGIN_PASSWORD_MIN_LENGTH) {
    return getErrorMessage('passwordMinLength');
  }
  return undefined;
}

/** Identifier field state: email format or phone length once the field is touched. */
export function getIdentifierFieldState(
  mode: LoginMode,
  identifier: string,
  touched: boolean,
): AuthFieldState {
  if (!touched) return 'default';
  if (!identifier.trim()) return 'error';
  if (mode === 'email' && !isValidEmail(identifier)) return 'error';
  if (mode === 'phone' && identifier.length < PHONE_IDENTIFIER_MIN_LENGTH) return 'error';
  return 'success';
}

/** Client-side identifier error once the field has been touched. */
export function getIdentifierFieldError(
  mode: LoginMode,
  identifier: string,
  touched: boolean,
): string | undefined {
  if (!touched) return undefined;
  if (!identifier.trim()) return getErrorMessage('fieldRequired');
  if (mode === 'email' && !isValidEmail(identifier)) return getErrorMessage('invalidEmail');
  return undefined;
}

/** Success when the address is valid; error only after blur. */
export function getEmailFieldState(email: string, touched: boolean): AuthFieldState {
  if (!touched) return 'default';
  if (email.trim() && isValidEmail(email)) return 'success';
  return 'error';
}

/** Prefer the server message; otherwise empty / invalid after touch. */
export function getEmailFieldError(
  email: string,
  touched: boolean,
  serverError?: string,
): string | undefined {
  if (serverError) return serverError;
  if (!touched) return undefined;
  if (!email.trim()) return getErrorMessage('fieldRequired');
  if (!isValidEmail(email)) return getErrorMessage('invalidEmail');
  return undefined;
}

/** Password field state from the setup meter once touched. */
export function getSetupPasswordFieldState(valid: boolean, touched: boolean): AuthFieldState {
  if (!touched) return 'default';
  return valid ? 'success' : 'error';
}

/** Criteria-failed message after the setup password field is touched. */
export function getSetupPasswordFieldError(valid: boolean, touched: boolean): string | undefined {
  if (!touched || valid) return undefined;
  return getErrorMessage('passwordCriteriaIncomplete');
}

/** Confirm field succeeds only when it matches a valid password. */
export function getConfirmPasswordFieldState(
  confirmPassword: string,
  password: string,
  passwordValid: boolean,
  touched: boolean,
): AuthFieldState {
  if (!touched) return 'default';
  if (confirmPassword === password && passwordValid) return 'success';
  return 'error';
}

/** Server error first; else mismatch / criteria after touch. */
export function getConfirmPasswordFieldError(
  confirmPassword: string,
  password: string,
  passwordValid: boolean,
  touched: boolean,
  serverError?: string,
): string | undefined {
  if (serverError) return serverError;
  if (!touched) return undefined;
  if (confirmPassword !== password) return getErrorMessage('passwordsMismatch');
  if (!passwordValid) return getErrorMessage('passwordCriteriaIncomplete');
  return undefined;
}

/** Patch one field on the invitation password form. */
export function patchSetupPassword(
  prev: SetupPasswordData,
  field: keyof SetupPasswordData,
  value: string,
): SetupPasswordData {
  return { ...prev, [field]: value };
}

/** Wordmark for the current theme, falling back to the opposite theme asset. */
export function resolveThemeLogo(logos: BrandingLogos, isDarkMode: boolean): string {
  const mode = isDarkMode ? 'dark' : 'light';
  const altMode = isDarkMode ? 'light' : 'dark';
  return logos.logo1[mode] || logos.logo1[altMode] || '';
}

/** Detect an international prefix and return the national number for phone mode. */
export function matchPhonePrefix(value: string): PhonePrefixMatch | null {
  if (!value.startsWith('+')) return null;
  const afterPlus = value.slice(1);
  for (const dialCode of SORTED_DIAL_CODES) {
    if (!afterPlus.startsWith(dialCode)) continue;
    return {
      country: COUNTRY_CODE_MAP[dialCode],
      nationalNumber: afterPlus.slice(dialCode.length).replace(/^\s+/, ''),
    };
  }
  return null;
}

/** CSS classes for one login background particle. */
export function loginParticleClass(index: number): string {
  return `particle particle-${(index % 3) + 1} particle-index-${index}`;
}

/** CSS classes for one 404 / 403 decorative particle. */
export function unauthParticleClass(variant: 'notfound' | 'unauth', index: number): string {
  return `unauth-particle ${variant}-particle-${index + 1}`;
}
